import { useEffect, useState, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Typography, Box, IconButton,
  FormControl, InputLabel, Select, MenuItem,
  FormHelperText, Snackbar, Alert,
  CircularProgress, Backdrop, Autocomplete
} from '@mui/material';
import { Close, Add, Delete, AccountBalance, Warning } from '@mui/icons-material';
import { useMobileSpacing } from '../../../utils/mobileSpacing';
import { formatDate } from '../../../utils/dateUtils';

const JournalEntryModal = ({
  open,
  onClose,
  modalAction,
  formData,
  setFormData,
  journalEntryDetails,
  setJournalEntryDetails,
  onSave,
  masters,
  gstRates,
  loading,
  companyId,
  token,
  fetchMaxBillNo,
  branchId,
  fromDate,
  toDate
}) => {
  const mobileSpacing = useMobileSpacing();
  const isMobile = mobileSpacing.isMobile;

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  const isViewMode = modalAction === 'view';

  // Dynamic theme based on type — Credit = green, Debit = amber, View = slate
  const entryType = formData.IsType; // 'Credit' | 'Debit' | ''
  const themeConfig = {
    credit: { primary: '#429178', secondary: '#047857', light: 'rgba(66,145,120,0.1)', bg: 'rgba(66,145,120,0.05)' },
    debit:  { primary: '#dc6363', secondary: '#b91c1c', light: 'rgba(220,99,99,0.1)',  bg: 'rgba(220,99,99,0.05)' }
  };
  const currentTheme = entryType === 'Credit' ? themeConfig.credit : entryType === 'Debit' ? themeConfig.debit : { primary: '#2C3E6E', secondary: '#1a2a4a', light: 'rgba(44,62,110,0.1)', bg: 'rgba(44,62,110,0.05)' };

  const headerBg    = isViewMode ? '#1e293b'  : (entryType === 'Credit' ? '#f0fdf4' : entryType === 'Debit' ? '#fdf2f2' : '#f8fafc');
  const headerBorder = isViewMode ? '#e2e8f0' : (entryType === 'Credit' ? '#d1fae5' : entryType === 'Debit' ? '#fecaca' : '#e2e8f0');
  const headerTextColor = isViewMode ? '#f8fafc' : (entryType === 'Credit' ? '#064e3b' : entryType === 'Debit' ? '#7f1d1d' : '#1e293b');
  const iconColor   = isViewMode ? '#f8fafc'  : currentTheme.primary;
  const sectionBg   = isViewMode ? '#f1f5f9'  : (entryType === 'Credit' ? '#f0fdf4' : entryType === 'Debit' ? '#fdf2f2' : 'rgba(44,62,110,0.04)');
  const contentBg   = '#f9fafb';
  const actionsBg   = isViewMode ? '#f8fafc'  : (entryType === 'Credit' ? '#f0fdf4' : entryType === 'Debit' ? '#fdf2f2' : '#f8fafc');
  const actionsBorder = isViewMode ? '1px solid #e2e8f0' : (entryType === 'Credit' ? '1px solid #d1fae5' : entryType === 'Debit' ? '1px solid #fecaca' : '1px solid #e2e8f0');

  const compactInput = {
    '& .MuiInputBase-root': { height: '32px' },
    '& .MuiInputBase-input': { py: 0, fontSize: '0.8rem' },
    '& .MuiInputLabel-root': { fontSize: '0.8rem', lineHeight: '1rem' },
    '& .MuiSelect-select': { py: 0, fontSize: '0.8rem' }
  };

  useEffect(() => {
    if (open) {
      setErrors({});
      setTouched({});
      setSnackbar({ open: false, message: '', severity: 'error' });
      setHasUnsavedChanges(false);
      setCancelConfirmOpen(false);
      setModalLoading(true);
      const timer = setTimeout(() => setModalLoading(false), 300);
      return () => clearTimeout(timer);
    } else {
      setModalLoading(false);
    }
  }, [open]);

  const calculateTotals = useCallback(() => {
    const totalChildAmount = journalEntryDetails.reduce((sum, d) => sum + (parseFloat(d.Amount) || 0), 0);
    setFormData(prev => ({ ...prev, TotalChildAmount: totalChildAmount }));
  }, [journalEntryDetails, setFormData]);

  useEffect(() => {
    if (journalEntryDetails.length > 0) {
      const id = setTimeout(() => calculateTotals(), 150);
      return () => clearTimeout(id);
    }
  }, [journalEntryDetails, calculateTotals]);

  const showError = (msg) => setSnackbar({ open: true, message: msg, severity: 'error' });
  const handleCloseSnackbar = () => setSnackbar(prev => ({ ...prev, open: false }));

  const handleCancelClick = () => {
    if (hasUnsavedChanges && !isViewMode) setCancelConfirmOpen(true);
    else onClose();
  };
  const handleConfirmCancel = () => { setCancelConfirmOpen(false); setHasUnsavedChanges(false); onClose(); };
  const handleStayOnPage = () => setCancelConfirmOpen(false);

  const validateField = (field, value) => {
    switch (field) {
      case 'BillNumber': return (!value || value.toString().trim() === '') ? 'Journal Entry No is required' : '';
      case 'BillDate': return !value ? 'Date is required' : '';
      case 'IsType': return !value ? 'Type is required' : '';
      case 'MasterId': return !value ? 'Main Head is required' : '';
      case 'Amount': return (!value || parseFloat(value) <= 0) ? 'Amount must be greater than 0' : '';
      default: return '';
    }
  };

  const validateForm = () => {
    const newErrors = {};
    ['BillNumber', 'BillDate', 'IsType', 'MasterId', 'Amount'].forEach(f => {
      const err = validateField(f, formData[f]);
      if (err) newErrors[f] = err;
    });
    if (journalEntryDetails.length === 0) {
      newErrors.journalEntryDetails = 'At least one child entry is required';
    } else {
      const childSum = journalEntryDetails.reduce((sum, d) => sum + (parseFloat(d.Amount) || 0), 0);
      const diff = Math.abs(childSum - (parseFloat(formData.Amount) || 0));
      if (diff > 0.01) newErrors.journalEntryDetails = 'Total child amount must equal Journal Entry Amount';
      journalEntryDetails.forEach((d, i) => {
        if (!d.MasterId) newErrors.journalEntryDetails = `Child Head is required for entry ${i + 1}`;
        if (d.MasterId === formData.MasterId) newErrors.journalEntryDetails = `Child Head cannot be same as Main Head for entry ${i + 1}`;
        if (!d.Amount || parseFloat(d.Amount) <= 0) newErrors.journalEntryDetails = `Amount must be > 0 for entry ${i + 1}`;
      });
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    setErrors(prev => ({ ...prev, [field]: validateField(field, formData[field]) }));
  };

  const handleSaveWithValidation = () => {
    setTouched({ BillNumber: true, BillDate: true, IsType: true, MasterId: true, Amount: true, journalEntryDetails: true });
    const msgs = [];
    if (!formData.BillNumber || formData.BillNumber.toString().trim() === '') msgs.push('Journal Entry No is required');
    if (!formData.BillDate) msgs.push('Date is required');
    if (!formData.IsType) msgs.push('Type is required');
    if (!formData.MasterId) msgs.push('Main Head is required');
    if (!formData.Amount || parseFloat(formData.Amount) <= 0) msgs.push('Amount must be greater than 0');
    if (journalEntryDetails.length === 0) {
      msgs.push('At least one child entry is required');
    } else {
      const childSum = journalEntryDetails.reduce((sum, d) => sum + (parseFloat(d.Amount) || 0), 0);
      const diff = Math.abs(childSum - (parseFloat(formData.Amount) || 0));
      if (diff > 0.01) msgs.push('Total child amount must equal Journal Entry Amount');
      journalEntryDetails.forEach((d, i) => {
        if (!d.MasterId) msgs.push(`Child Head is required for entry ${i + 1}`);
        if (d.MasterId === formData.MasterId) msgs.push(`Child Head cannot be same as Main Head for entry ${i + 1}`);
        if (!d.Amount || parseFloat(d.Amount) <= 0) msgs.push(`Amount must be > 0 for entry ${i + 1}`);
      });
    }
    if (msgs.length > 0) { showError(msgs.join(' | ')); validateForm(); return; }
    if (validateForm()) onSave();
  };

  const handleChange = (field, value) => {
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
    if (!isViewMode) setHasUnsavedChanges(true);
    if (field === 'MasterId' && value) {
      const updated = journalEntryDetails.map(d => d.MasterId === value ? { ...d, MasterId: '' } : d);
      if (JSON.stringify(updated) !== JSON.stringify(journalEntryDetails)) {
        setJournalEntryDetails(updated);
        showError('Child entries with the same head as Main Head have been cleared.');
      }
    }
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleDetailChange = (index, field, value) => {
    const updated = [...journalEntryDetails];
    updated[index] = { ...updated[index], [field]: value };
    setJournalEntryDetails(updated);
    setHasUnsavedChanges(true);
  };

  const handleAddDetail = () => {
    setJournalEntryDetails([...journalEntryDetails, { Id: 0, MasterId: '', Amount: 0, Remarks: '' }]);
    setHasUnsavedChanges(true);
  };

  const handleRemoveDetail = (index) => {
    setJournalEntryDetails(journalEntryDetails.filter((_, i) => i !== index));
    setHasUnsavedChanges(true);
  };

  const formatCurrency = (amount) =>
    `₹${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const childSum = journalEntryDetails.reduce((sum, d) => sum + (parseFloat(d.Amount) || 0), 0);
  const journalAmount = parseFloat(formData.Amount) || 0;
  const difference = Math.abs(journalAmount - childSum);
  const isBalanced = difference <= 0.01 && journalEntryDetails.length > 0;

  const modalTitle = modalAction === 'create' ? 'Create Journal Entry'
    : modalAction === 'edit' ? 'Edit Journal Entry' : 'View Journal Entry';

  return (
    <>
      <Dialog
        open={open}
        onClose={(e, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            if (hasUnsavedChanges && !isViewMode) { setCancelConfirmOpen(true); return; }
          }
          onClose();
        }}
        maxWidth="lg" fullWidth fullScreen={isMobile} disableEscapeKeyDown
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : '10px', m: isMobile ? 0 : 1.5, maxWidth: '1000px', position: 'relative', maxHeight: isMobile ? '100%' : '92vh' } }}
      >
        {/* Loading Overlay */}
        <Backdrop sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, bgcolor: 'rgba(255,255,255,0.9)', zIndex: 9999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }} open={modalLoading}>
          <CircularProgress size={40} sx={{ color: currentTheme.primary }} />
          <Typography variant="body2" sx={{ mt: 2, color: currentTheme.primary, fontWeight: 600 }}>Loading Journal Entry...</Typography>
        </Backdrop>

        {/* Header */}
        <DialogTitle sx={{ py: 0.75, px: 2, bgcolor: headerBg, borderBottom: `1px solid ${headerBorder}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccountBalance sx={{ color: iconColor, fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: headerTextColor, fontSize: isMobile ? '0.9rem' : '1rem', fontFamily: 'Times New Roman, serif' }}>
                {modalTitle}
              </Typography>
            </Box>
            <IconButton onClick={handleCancelClick} size="small" sx={{ p: 0.5, color: iconColor }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: isMobile ? 1 : 1.5, bgcolor: contentBg }}>
          {/* Journal Entry Info */}
          <Typography variant="caption" sx={{ fontWeight: 600, color: isViewMode ? '#475569' : currentTheme.primary, display: 'block', mb: 0.5 }}>
            Journal Entry Information
          </Typography>

          <Box sx={{ bgcolor: sectionBg, p: 1, borderRadius: 1, mb: 1 }}>
            <Grid container spacing={1}>
              <Grid item xs={6} sm={1.5}>
                <TextField fullWidth size="small" label="Journal Entry No *"
                  value={formData.BillNumber || ''} onChange={(e) => handleChange('BillNumber', e.target.value)}
                  onBlur={() => handleBlur('BillNumber')} disabled={isViewMode}
                  error={touched.BillNumber && !!errors.BillNumber} helperText={touched.BillNumber && errors.BillNumber}
                  sx={compactInput} />
              </Grid>
              <Grid item xs={6} sm={2}>
                {isViewMode ? (
                  <TextField fullWidth size="small" label="Date"
                    value={formatDate(formData.BillDate) || '-'} disabled
                    InputLabelProps={{ shrink: true }} sx={compactInput} />
                ) : (
                  <TextField fullWidth size="small" type="date" label="Date *"
                    value={formData.BillDate || ''} onChange={(e) => handleChange('BillDate', e.target.value)}
                    onBlur={() => handleBlur('BillDate')}
                    error={touched.BillDate && !!errors.BillDate} helperText={touched.BillDate && errors.BillDate}
                    InputLabelProps={{ shrink: true }} sx={compactInput} />
                )}
              </Grid>
              <Grid item xs={6} sm={1.5}>
                <FormControl fullWidth size="small" error={touched.IsType && !!errors.IsType} sx={compactInput}>
                  <InputLabel>Type *</InputLabel>
                  <Select value={formData.IsType || ''} onChange={(e) => handleChange('IsType', e.target.value)}
                    onBlur={() => handleBlur('IsType')} disabled={isViewMode} label="Type *"
                    MenuProps={{ PaperProps: { sx: { '& .MuiMenuItem-root': { fontSize: '0.85rem' } } } }}>
                    <MenuItem value="Credit" sx={{ color: themeConfig.credit.primary, fontWeight: 600 }}>Credit</MenuItem>
                    <MenuItem value="Debit" sx={{ color: themeConfig.debit.primary, fontWeight: 600 }}>Debit</MenuItem>
                  </Select>
                  {touched.IsType && errors.IsType && <FormHelperText>{errors.IsType}</FormHelperText>}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={5}>
                {isViewMode ? (
                  <TextField fullWidth size="small" label="Main Head"
                    value={masters.find(m => m.Id === formData.MasterId)?.Name || '-'} disabled
                    sx={compactInput} />
                ) : (
                  <Autocomplete
                    size="small"
                    freeSolo
                    options={masters}
                    getOptionLabel={(option) => {
                      if (typeof option === 'string') return option;
                      return option?.Name || '';
                    }}
                    value={masters.find(m => m.Id === parseInt(formData.MasterId)) || null}
                    onChange={(event, newValue) => {
                      if (newValue && typeof newValue === 'object') {
                        handleChange('MasterId', newValue.Id);
                      } else {
                        handleChange('MasterId', '');
                      }
                    }}
                    filterOptions={(options, { inputValue }) => {
                      if (!inputValue) return options;
                      const filterText = inputValue.toLowerCase();
                      return options.filter(option =>
                        (option.Name || '').toLowerCase().includes(filterText)
                      );
                    }}
                    isOptionEqualToValue={(option, value) => option.Id === value?.Id}
                    renderOption={(props, option) => (
                      <li {...props} key={option.Id}>
                        {option.Name}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="Main Head *"
                        error={touched.MasterId && !!errors.MasterId}
                        helperText={touched.MasterId && errors.MasterId}
                        onBlur={() => handleBlur('MasterId')}
                        sx={{
                          '& .MuiInputBase-root': { height: '32px', minHeight: '32px', padding: '0 9px !important' },
                          '& .MuiInputBase-input': { padding: '0 !important', fontSize: '0.8rem', height: '32px' },
                          '& .MuiInputLabel-root': { fontSize: '0.8rem', lineHeight: '1rem' },
                          '& .MuiAutocomplete-endAdornment': { top: 'calc(50% - 12px)' }
                        }}
                      />
                    )}
                    ListboxProps={{ style: { maxHeight: 200, fontSize: '0.85rem' } }}
                    sx={{ width: '100%' }}
                  />
                )}
              </Grid>
              <Grid item xs={6} sm={1.5}>
                <TextField fullWidth size="small" type="number" label="Amount *"
                  value={formData.Amount || ''} onChange={(e) => handleChange('Amount', e.target.value)}
                  onBlur={() => handleBlur('Amount')} disabled={isViewMode}
                  error={touched.Amount && !!errors.Amount} helperText={touched.Amount && errors.Amount}
                  inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                  sx={{ ...compactInput, '& .MuiInputBase-root': { height: '32px' }, '& .MuiInputBase-input': { textAlign: 'right', py: 0, fontSize: '0.8rem' } }} />
              </Grid>
              <Grid item xs={6} sm={1}>
                <Box sx={{ height: '32px', display: 'flex', alignItems: 'center', border: `1px solid ${isBalanced ? '#d1fae5' : '#e5e7eb'}`, borderRadius: 1, bgcolor: isBalanced ? '#f0fdf4' : '#f9fafb', px: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 600, color: isBalanced ? '#047857' : '#6b7280', whiteSpace: 'nowrap' }}>
                    ∑ {formatCurrency(childSum)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Remarks */}
          <Box sx={{ bgcolor: sectionBg, p: 1, borderRadius: 1, mb: 1 }}>
            <TextField fullWidth size="small" label="Remarks"
              value={formData.Remarks || ''} onChange={(e) => handleChange('Remarks', e.target.value)}
              disabled={isViewMode} multiline rows={1}
              sx={{ '& .MuiInputBase-root': { height: 'auto' }, '& .MuiInputBase-input': { fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.8rem' } }} />
          </Box>

          {/* Child Entries */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: isViewMode ? '#475569' : currentTheme.primary }}>
              Child Entries
            </Typography>
            {!isViewMode && (
              <Button variant="contained" size="small" onClick={handleAddDetail} startIcon={<Add />}
                sx={{ bgcolor: currentTheme.primary, '&:hover': { bgcolor: currentTheme.secondary }, textTransform: 'none', fontSize: '0.75rem', height: 28 }}>
                Add Child Entry
              </Button>
            )}
          </Box>

          <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, mb: 1 }}>
            <Box sx={{ maxHeight: 240, overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
                  <tr style={{ background: isViewMode ? 'linear-gradient(135deg, #334155 0%, #1e293b 100%)' : `linear-gradient(135deg, ${currentTheme.primary} 0%, ${currentTheme.secondary} 100%)` }}>
                    <th style={{ padding: '6px 8px', textAlign: 'left', color: 'white', fontWeight: 600, width: '40%' }}>Child Head</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right', color: 'white', fontWeight: 600, width: '20%' }}>Amount</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', color: 'white', fontWeight: 600, width: '30%' }}>Remarks</th>
                    {!isViewMode && <th style={{ padding: '6px 8px', textAlign: 'center', color: 'white', fontWeight: 600, width: '10%' }}>Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {journalEntryDetails.length === 0 ? (
                    <tr>
                      <td colSpan={isViewMode ? 3 : 4} style={{ padding: '16px', textAlign: 'center', color: '#999', fontSize: '0.8rem' }}>
                        No child entries. Click "Add Child Entry" to begin.
                      </td>
                    </tr>
                  ) : (
                    journalEntryDetails.map((detail, index) => (
                      <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#f8f9fa' : '#ffffff', borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '4px 6px' }}>
                          {isViewMode ? (
                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                              {masters.find(m => m.Id === detail.MasterId)?.Name || '-'}
                            </Typography>
                          ) : (
                            <Autocomplete
                              size="small"
                              freeSolo
                              options={masters.filter(m => m.Id !== formData.MasterId)}
                              getOptionLabel={(option) => {
                                if (typeof option === 'string') return option;
                                return option?.Name || '';
                              }}
                              value={masters.find(m => m.Id === detail.MasterId) || null}
                              onChange={(event, newValue) => {
                                if (newValue && typeof newValue === 'object') {
                                  handleDetailChange(index, 'MasterId', newValue.Id);
                                } else {
                                  handleDetailChange(index, 'MasterId', '');
                                }
                              }}
                              filterOptions={(options, { inputValue }) => {
                                if (!inputValue) return options;
                                const filterText = inputValue.toLowerCase();
                                return options.filter(option =>
                                  (option.Name || '').toLowerCase().includes(filterText)
                                );
                              }}
                              isOptionEqualToValue={(option, value) => option.Id === value?.Id}
                              renderOption={(props, option) => (
                                <li {...props} key={option.Id}>
                                  {option.Name}
                                </li>
                              )}
                              renderInput={(params) => (
                                <TextField {...params} placeholder="Select head" sx={{
                                  '& .MuiInputBase-root': { height: '26px', minHeight: '26px', padding: '0 9px !important' },
                                  '& .MuiInputBase-input': { padding: '0 !important', fontSize: '0.75rem', height: '26px' },
                                  '& .MuiInputLabel-root': { fontSize: '0.75rem' },
                                  '& .MuiAutocomplete-endAdornment': { top: 'calc(50% - 12px)' }
                                }} />
                              )}
                              ListboxProps={{ style: { maxHeight: 200, fontSize: '0.8rem' } }}
                              sx={{ width: '100%' }}
                            />
                          )}
                        </td>
                        <td style={{ padding: '4px 6px' }}>
                          {isViewMode ? (
                            <Typography variant="body2" sx={{ fontSize: '0.75rem', textAlign: 'right' }}>{formatCurrency(detail.Amount)}</Typography>
                          ) : (
                            <TextField size="small" type="number" value={detail.Amount || ''}
                              onChange={(e) => handleDetailChange(index, 'Amount', e.target.value)}
                              inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right', fontSize: '0.75rem', padding: '4px' } }}
                              sx={{ width: '100%', '& .MuiOutlinedInput-root': { height: '26px' } }} />
                          )}
                        </td>
                        <td style={{ padding: '4px 6px' }}>
                          {isViewMode ? (
                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>{detail.Remarks || '-'}</Typography>
                          ) : (
                            <TextField size="small" value={detail.Remarks || ''}
                              onChange={(e) => handleDetailChange(index, 'Remarks', e.target.value)}
                              inputProps={{ style: { fontSize: '0.75rem', padding: '4px' } }}
                              sx={{ width: '100%', '& .MuiOutlinedInput-root': { height: '26px' } }} />
                          )}
                        </td>
                        {!isViewMode && (
                          <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                            <IconButton size="small" onClick={() => handleRemoveDetail(index)} sx={{ color: '#dc6363', p: '2px' }}>
                              <Delete sx={{ fontSize: 16 }} />
                            </IconButton>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
                {journalEntryDetails.length > 0 && (
                  <tfoot>
                    <tr style={{ background: '#f1f5f9', borderTop: '2px solid #e0e0e0' }}>
                      <td style={{ padding: '6px 8px', fontWeight: 700, fontSize: '0.75rem', color: '#333' }}>Totals</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, fontSize: '0.75rem', color: '#333' }}>{formatCurrency(childSum)}</td>
                      <td colSpan={isViewMode ? 1 : 2} style={{ padding: '6px 8px', textAlign: 'right', fontSize: '0.72rem', fontWeight: 600, color: isBalanced ? '#047857' : '#dc6363' }}>
                        {isBalanced ? '✓ Balanced' : `Diff: ${formatCurrency(difference)}`}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </Box>
          </Box>

          {errors.journalEntryDetails && (
            <Typography variant="caption" sx={{ color: '#dc6363', fontWeight: 500, display: 'block', mb: 1 }}>
              {errors.journalEntryDetails}
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1, borderTop: actionsBorder, bgcolor: actionsBg, justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={handleCancelClick} variant="outlined" size="small" sx={{ textTransform: 'none', fontSize: '0.8rem' }}>Cancel</Button>
          {!isViewMode && (
            <Button onClick={handleSaveWithValidation} variant="contained" size="small" disabled={loading}
              sx={{ bgcolor: currentTheme.primary, '&:hover': { bgcolor: currentTheme.secondary }, textTransform: 'none', fontSize: '0.8rem' }}>
              {loading ? <><CircularProgress size={12} sx={{ color: 'white', mr: 0.5 }} />{modalAction === 'edit' ? 'Updating...' : 'Saving...'}</> : modalAction === 'edit' ? 'Update' : 'Save'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Cancel Confirmation */}
      <Dialog open={cancelConfirmOpen} onClose={handleStayOnPage} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '12px', p: 1 } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ bgcolor: '#fef3c7', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Warning sx={{ color: '#f59e0b', fontSize: 24 }} />
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b' }}>Unsaved Changes</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#64748b', fontSize: '0.9rem', mb: 0.5 }}>You have unsaved changes that will be lost if you leave.</Typography>
          <Typography sx={{ color: '#dc6363', fontSize: '0.85rem', fontWeight: 600 }}>Are you sure you want to exit without saving?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <Button onClick={handleStayOnPage} variant="outlined" size="small" sx={{ textTransform: 'none' }}>Stay on Page</Button>
          <Button onClick={handleConfirmCancel} variant="contained" size="small" sx={{ bgcolor: '#dc6363', '&:hover': { bgcolor: '#b91c1c' }, textTransform: 'none' }}>Exit Without Saving</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </>
  );
};

export default JournalEntryModal;

